import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'hooks/store';
import {
  clientSeedsItemsSelector,
  clientSeedsRemoteTotalSelector,
  isLoadingClientSeedsSelector,
  getClientSeedInventoriesListAction,
  cleanClientSeedsAction,
} from 'state/slices/clients';
import { usePagination } from 'hooks/pagination';
import {
  InventoryOwner,
  PaginationData,
  SeedInventorySummary,
  Variety,
} from 'constants/interface';
import { Search, Table } from 'UI';

import {
  SearchContainer,
} from './style';

interface InventoryViewProps {
  shouldReload: boolean
  onReloadFinish(): void
}

const InventoryView = ({
  shouldReload,
  onReloadFinish,
}: InventoryViewProps): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(clientSeedsRemoteTotalSelector);

  const inventoryItems = useSelector(clientSeedsItemsSelector);
  const isRemoteLoading = useSelector(isLoadingClientSeedsSelector);

  const inventories = inventoryItems.map((inventory) => {
    const tableInventory: SeedInventorySummary & {
      key?: string,
    } = {
      ...inventory,
      key: `${inventory.variety.id.toString()}/${inventory.owner.id.toString()}`,
    };
    return tableInventory;
  }).filter((tableInventory): tableInventory is SeedInventorySummary => !!tableInventory);

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    const orderByMapping: Record<string, string> = {
      owner: 'client',
    };
    const orderBy = orderByMapping[paginationData.orderBy || ''] || paginationData.orderBy;
    dispatch(getClientSeedInventoriesListAction({
      ...paginationData,
      orderBy,
    }));
  };
  const pagination = usePagination<SeedInventorySummary>(
    remoteFetch,
    {
      filter: {
        client: undefined,
        variety: undefined,
      },
    },
  );

  useEffect(() => {
    dispatch(cleanClientSeedsAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  useEffect(() => {
    if (shouldReload) {
      pagination.fetch();
      onReloadFinish();
    }
  }, [shouldReload]);

  const sorter = (): number => 0;

  const pageData = pagination.getInternal();

  return (
    <>
      <SearchContainer>
        <Search placeholder="Buscar no estoque" onSearch={pagination.onSearch} allowClear />
      </SearchContainer>
      <Table
        dataSource={inventories}
        pagination={pagination.getInternal()}
        loading={isRemoteLoading}
        showSorterTooltip={false}
        locale={{
          filterReset: 'Limpar',
          cancelSort: 'Clique para limpar ordenação',
          triggerAsc: 'Clique para ordem crescente',
          triggerDesc: 'Clique para ordem decrescente',
        }}
        onChange={pagination.onTableChange}
      >
        <Table.Column
          title="Cliente"
          key="client"
          dataIndex="owner"
          render={(
            owner: InventoryOwner,
          ) => owner.name}
          sorter={sorter}
          sortOrder={pageData.orderBy === 'owner' ? pageData.order : undefined}
        />
        <Table.Column
          title="Cultura"
          key="culture"
          dataIndex="variety"
          render={(variety: Variety) => `${variety.culture.name}`}
          sorter={sorter}
          sortOrder={pageData.orderBy === 'culture' ? pageData.order : undefined}
        />
        <Table.Column
          title="Variedade"
          key="variety"
          dataIndex="variety"
          render={(variety: Variety) => `${variety.name}`}
          sorter={sorter}
          sortOrder={pageData.orderBy === 'variety' ? pageData.order : undefined}
        />
        <Table.Column
          title="Saldo atual"
          key="quantity"
          dataIndex="quantity"
          render={(quantity: number) => {
            const formattedQuantity = Intl
              .NumberFormat(
                'pt-BR',
              ).format(quantity);
            const content = `${formattedQuantity} sementes`;
            return content;
          }}
        />
      </Table>
    </>
  );
};

export default InventoryView;
